import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import './Login.css';
import { useAuth } from './AuthContext';
import { hasAllModuleAccessEmail, hasExplicitFireAccessEmail, hasFlhaFirstLandingEmail } from './utils/fireAccess';
import { getAppPath } from './utils/runtimeMode';

const Login = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();
    const [error, setError] = useState('');

    const resolvePostLoginTarget = () => {
        const searchParams = new URLSearchParams(location.search);
        const redirectParam = searchParams.get('redirect');
        const storedRedirect = sessionStorage.getItem('redirectAfterLogin');

        const candidates = [redirectParam, storedRedirect]
            .map((value) => {
                if (!value) return '';

                try {
                    return decodeURIComponent(value);
                } catch (error) {
                    return value;
                }
            })
            .map((value) => String(value || '').trim())
            .filter(Boolean);

        for (const candidate of candidates) {
            if (candidate.startsWith('/')) {
                return candidate;
            }
        }

        return '';
    };

    useEffect(() => {
        // Check URL for logout parameter
        const params = new URLSearchParams(window.location.search);
        if (params.has('loggedout') || document.referrer.includes('/logout')) {
            // Redirect to homepage instead of showing login page
            window.location.href = getAppPath('/');
            return;
        }
    }, []);

    useEffect(() => {
        // Clear any auth data when loading the login page with loggedout parameter
        if (window.location.search.includes('loggedout=true')) {
            localStorage.clear();
            sessionStorage.clear();
        }
        
        // Optional: Only check auth status if not coming from logout
        if (!window.location.search.includes('loggedout=true')) {
            // Your existing auth check
        }
    }, []);

    const initialValues = {
        username: '',
        password: '',
    };

    const validationSchema = Yup.object({
        username: Yup.string().required('Required'),
        password: Yup.string().required('Required'),
    });

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            setError('');

            // Use the login function from AuthContext
            const success = await login({
                email: values.username, // Map 'username' field to 'email'
                password: values.password,
            });

            if (success) {
                const postLoginTarget = resolvePostLoginTarget();
                if (postLoginTarget) {
                    sessionStorage.removeItem('redirectAfterLogin');
                    navigate(postLoginTarget, { replace: true });
                    return;
                }

                let jobTitle = '';
                let departmentId = '';
                let departmentIds = [];
                let hasFireDepartmentAccess = false;
                let hasNonFireDepartmentAccess = false;
                try {
                    const saved = localStorage.getItem('user');
                    const parsed = saved ? JSON.parse(saved) : {};
                    jobTitle = String(parsed?.jobTitle || parsed?.TITLE || parsed?.title || '').trim();
                    departmentId = String(parsed?.departmentId ?? parsed?.DEPARTMENT_ID ?? parsed?.department_id ?? '').trim();
                    departmentIds = Array.isArray(parsed?.departmentIds)
                        ? parsed.departmentIds
                        : Array.isArray(parsed?.DEPARTMENT_IDS)
                            ? parsed.DEPARTMENT_IDS
                            : [];
                    hasFireDepartmentAccess = !!(parsed?.hasFireDepartmentAccess ?? parsed?.HAS_FIRE_DEPARTMENT_ACCESS);
                    hasNonFireDepartmentAccess = !!(parsed?.hasNonFireDepartmentAccess ?? parsed?.HAS_NON_FIRE_DEPARTMENT_ACCESS);
                } catch (e) {
                    jobTitle = '';
                    departmentId = '';
                    departmentIds = [];
                    hasFireDepartmentAccess = false;
                    hasNonFireDepartmentAccess = false;
                }

                const normalized = jobTitle.toLowerCase().replace(/[\s_-]+/g, '');
                const deptIdNum = Number(departmentId);
                const hasFireDepartmentId = departmentIds.some((id) => Number(String(id).trim()) === 6);
                const hasAllModuleAccess = hasAllModuleAccessEmail(values.username);
                const shouldLandOnFlhaFirst = hasFlhaFirstLandingEmail(values.username);
                const isFireDept = hasFireDepartmentAccess || hasFireDepartmentId || hasExplicitFireAccessEmail(values.username) || (!Number.isNaN(deptIdNum) && departmentId !== '' && deptIdNum === 6);
                const isFireOnlyUser = isFireDept && !hasNonFireDepartmentAccess && !hasAllModuleAccess;

                if (shouldLandOnFlhaFirst || ((normalized === 'volunteerfirefighter' || isFireDept) && isFireOnlyUser)) {
                    navigate('/fire-training-flha', { replace: true });
                    return;
                }

                navigate('/acknowledgments');
            } else {
                setError('Login failed. Please check your credentials.');
            }
        } catch (error) {
            console.error('Login error:', error);
            setError('An unexpected error occurred. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="login-container">
            <div className="login-form">
                <h2>Login</h2>
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ isSubmitting }) => (
                        <Form>
                            <div className="form-group">
                                <label htmlFor="username">Username</label>
                                <Field type="text" id="username" name="username" />
                                <ErrorMessage name="username" component="div" className="error-message" />
                            </div>
                            <div className="form-group">
                                <label htmlFor="password">Password</label>
                                <Field type="password" id="password" name="password" />
                                <ErrorMessage name="password" component="div" className="error-message" />
                            </div>
                            <button type="submit" disabled={isSubmitting}>Login</button>
                        </Form>
                    )}
                </Formik>
                {error && <div className="error-message">{error}</div>}

                <div className="forgot-password-link">
                    <Link to="/forgot-password">Forgot Password?</Link>
                </div>
            </div>
        </div>
    );
};

export default Login;
